/**
 * Pure helpers for payment gates on the recapitulatif step.
 * Extracted from Recap.tsx so autonomous-registration flows stay regression-tested.
 */

export type FundingOrganismRef = {
  code?: string | null
  type?: string | null
} | null

export type BillingInfo = {
  invoice_company_name?: string | null
  invoice_first_name?: string | null
  invoice_last_name?: string | null
  invoice_street?: string | null
  invoice_postal_code?: string | null
  invoice_city?: string | null
}

export const isBillingInfoComplete = (billing: BillingInfo): boolean => {
  const hasIdentity = Boolean(
    billing.invoice_company_name || (billing.invoice_first_name && billing.invoice_last_name),
  )
  const hasAddress = Boolean(billing.invoice_street && billing.invoice_postal_code && billing.invoice_city)
  return hasIdentity && hasAddress
}

/**
 * Whether the prospect must go through a payment step (invoice + pay CTA).
 * Mirrors Recap.tsx shouldPay.
 */
export const shouldPay = (
  fundingOrganism: FundingOrganismRef,
  personalFunding: number | null,
  defaultPersonalFunding: number | null | undefined,
): boolean => {
  const code = fundingOrganism?.code
  return (
    code === 'ALMA' ||
    code === 'STRIPE' ||
    (personalFunding !== null && personalFunding > 0) ||
    (personalFunding === null && Boolean(defaultPersonalFunding))
  )
}

/**
 * Alma InPage amount in euros before * 100.
 * Note: Recap historically uses truthy checks, so personal_funding === 0 falls through to default.
 */
export const resolveAlmaPaymentAmount = (
  personalFunding: number | null | undefined,
  defaultPersonalFunding: number | null | undefined,
  listingPrice: number,
): number => {
  if (personalFunding) {
    return personalFunding
  }
  if (defaultPersonalFunding) {
    return defaultPersonalFunding
  }
  return listingPrice
}

export const toAlmaAmountInCents = (amountEuros: number): number => Math.round(amountEuros * 100)

/**
 * Max Alma installments offered in PersoFin (self-registration).
 */
export const almaMaxInstallments = (listingPrice: number | null | undefined): number => {
  if (!listingPrice) return 0
  if (listingPrice < 299) return 2
  if (listingPrice < 399) return 3
  return 4
}

export const almaInstallmentAmount = (amountToPay: number, installmentsCount: number): number => {
  if (!installmentsCount) return amountToPay
  return amountToPay / installmentsCount
}

export type GoCardlessInstallment = {
  due_date: string
  amount: number
  provider: string
  status: string
}

export const getGcScheduleSummary = (installments: GoCardlessInstallment[]) => {
  const sorted = [...installments].sort(
    (a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
  )
  const cardPayment = sorted.find((inst) => inst.provider === 'stripe') ?? null
  const sepaPayments = sorted.filter((inst) => inst.provider !== 'stripe')
  const total = sorted.reduce((acc, inst) => acc + inst.amount, 0)
  const lastSepaDate =
    sepaPayments.length > 0 ? sepaPayments[sepaPayments.length - 1].due_date : null

  return { sorted, cardPayment, sepaPayments, total, lastSepaDate }
}

export type RecapSubmitGateInput = {
  loading: boolean
  fundingOrganism: FundingOrganismRef
  preferredPaymentMethod?: string | null
  shouldPay: boolean
  billingInfoComplete: boolean
  /** Alma InPage widget ready */
  inPageReady?: boolean
  validated?: boolean
  stripeContractSignedAt?: string | null
  stripeCguCgv?: boolean
  persoProgram?: boolean
  persoCguCgv?: boolean
  persoSignature?: boolean
  persoCheckbox?: boolean
  dpcCguCgv?: boolean
  dpcContractSignedAt?: string | null
  opcoAmount?: number | null
  personalFunding?: number | null
  gcStatusLoading?: boolean
  gcActionLoading?: boolean
  gcMandateReady?: boolean
  gcCardRegistered?: boolean
}

/**
 * Pure version of Recap.isSubmitDisabled — prevents paying/validating with incomplete data.
 */
export const isRecapSubmitDisabled = (input: RecapSubmitGateInput): boolean => {
  if (input.loading) return true

  const code = input.fundingOrganism?.code ?? ''
  const preferedPaymentProvider = input.preferredPaymentMethod || 'stripe'

  if (code === 'GOCARDLESS') {
    if (input.shouldPay && !input.billingInfoComplete) return true
    const canSkipCard = Boolean(input.validated || input.gcCardRegistered)
    return Boolean(
      input.gcStatusLoading || input.gcActionLoading || (!canSkipCard && !input.gcMandateReady),
    )
  }

  if (preferedPaymentProvider === 'alma' || code === 'ALMA') {
    return !input.billingInfoComplete || !input.inPageReady
  }

  if (input.shouldPay && !input.billingInfoComplete) {
    return true
  }

  if (code === 'STRIPE') {
    return !(input.stripeContractSignedAt && input.billingInfoComplete && input.stripeCguCgv)
  }

  if (['FIFPL', 'FAFPM'].includes(code)) {
    return !(input.persoProgram && input.persoCguCgv && input.persoSignature && input.persoCheckbox)
  }

  if (code === 'DPC') {
    return !(input.dpcCguCgv && input.dpcContractSignedAt)
  }

  if (input.fundingOrganism?.type === 'opco') {
    return input.opcoAmount === null || !input.personalFunding
  }

  return false
}
